/**
 * BuildingStatsCard — Resumen de building_centroids en el panel lateral
 *
 * @remarks
 * Muestra total, asociados a red y no asociados.
 * No carga la lista completa de 439K edificios.
 */

import { useEffect, useState } from 'react';
import { buildingsApi, type BuildingStats } from './buildings-api';

interface BuildingStatsCardProps {
  projectId: string;
}

function fmt(n: number): string {
  return n.toLocaleString('es-AR');
}

export function BuildingStatsCard({ projectId }: BuildingStatsCardProps): JSX.Element {
  const [stats, setStats] = useState<BuildingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setError(null);

    buildingsApi.getStats(projectId)
      .then(setStats)
      .catch(() => setError('Error al cargar estadísticas'))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return (
      <div className="building-stats-card building-stats-loading">
        Cargando estadísticas…
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="building-stats-card building-stats-error">
        {error ?? 'Sin datos'}
      </div>
    );
  }

  const assocPct = stats.total > 0
    ? Math.round((stats.associated / stats.total) * 100)
    : 0;

  return (
    <div className="building-stats-card">
      <div className="building-stats-row building-stats-total">
        <span className="building-stats-label">Total edificios</span>
        <span className="building-stats-value">{fmt(stats.total)}</span>
      </div>
      <div className="building-stats-row building-stats-assoc">
        <span className="building-stats-label">Asociados a red</span>
        <span className="building-stats-value">{fmt(stats.associated)}</span>
      </div>
      <div className="building-stats-row building-stats-notassoc">
        <span className="building-stats-label">Sin asociar</span>
        <span className="building-stats-value">{fmt(stats.notAssociated)}</span>
      </div>
      <div className="building-stats-progress-wrap">
        <div
          className="building-stats-progress-bar"
          style={{ width: `${assocPct}%` }}
          title={`${assocPct}% asociados`}
        />
      </div>
      <div className="building-stats-source">
        Fuente: {stats.sourceDataset}
      </div>
    </div>
  );
}
